<template>
    <div class="card flex justify-end p-2 mb-4">
        <ul class="flex list-none m-0 p-0 gap-2 items-center">
            <li>
                <button type="button"
                    class="inline-flex w-8 h-8 p-0 items-center justify-center bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded cursor-pointer"
                    @click="onThemeToggler">
                    <i :class="`dark:text-white pi ${iconClass}`" />
                </button>
            </li>

            <li class="relative">
                <button type="button" @click="dropdownOpen = !dropdownOpen"
                    class="inline-flex w-8 h-8 p-0 items-center justify-center bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded cursor-pointer">
                    <i class="pi pi-palette dark:text-white"></i>
                </button>
                
                <div v-if="dropdownOpen"
                    class="absolute top-[2.5rem] right-0 w-[16rem] p-3 bg-white dark:bg-zinc-800 rounded-md shadow border border-gray-200 dark:border-zinc-700 flex-col justify-start items-start gap-3.5 inline-flex z-50">
                    
                    <div class="flex-col justify-start items-start gap-2 inline-flex pr-4">
                        <span class="text-sm font-medium">Primary Colors</span>
                        <div class="self-stretch justify-start items-start gap-2 inline-flex flex-wrap">
                            <button v-for="primaryColor of primaryColors" :key="primaryColor.name" type="button"
                                :title="primaryColor.name" @click="updateColors('primary', primaryColor)"
                                class="outline outline-2 outline-offset-1 outline-transparent cursor-pointer p-0 rounded-[50%] w-5 h-5"
                                :style="{
                                    backgroundColor: primaryColor.name === 'noir' ? '#000000' : primaryColor.palette['500'],
                                    outlineColor: selectedPrimaryColor === primaryColor.name ? 'var(--p-primary-color, #000)' : 'transparent',
                                }"></button>
                        </div>
                    </div>

                    <div class="flex-col justify-start items-start gap-2 inline-flex pr-2">
                        <span class="text-sm font-medium">Surface Colors</span>
                        <div class="self-stretch justify-start items-start gap-2 inline-flex flex-wrap">
                            <button v-for="surface of surfaces" :key="surface.name" type="button" :title="surface.name"
                                @click="updateColors('surface', surface)"
                                class="outline outline-2 outline-offset-1 outline-transparent cursor-pointer p-0 rounded-[50%] w-5 h-5"
                                :style="{
                                    backgroundColor: surface.palette['500'],
                                    outlineColor: selectedSurfaceColor === surface.name ? 'var(--p-primary-color, #000)' : 'transparent',
                                }"></button>
                        </div>
                    </div>

                    <div class="flex-col justify-start items-start gap-2 inline-flex w-full">
                        <span class="text-sm font-medium">Preset</span>
                        <div class="inline-flex p-1 items-start gap-1 rounded border border-gray-100 w-full">
                            <SelectButton v-model="currentTheme" @update:modelValue="onPresetChange"
                                :options="presets" :unselectable="false" class="w-full" />
                        </div>
                    </div>

                    <div class="inline-flex flex-col justify-start items-start gap-2 w-full pt-2">
                        <span class="text-sm font-medium m-0">Ripple Effect</span>
                        <ToggleSwitch :modelValue="rippleActive" @update:modelValue="onRippleChange" />
                    </div>
                </div>
            </li>
        </ul>
    </div>
</template>

<script>
import { $t, updatePreset, updateSurfacePalette } from '@primeuix/themes';
import Noir from '@/presets/Noir'; // 確保你的項目中有此檔案，若無可註釋掉
import SelectButton from 'primevue/selectbutton';
import ToggleSwitch from 'primevue/toggleswitch';

const presets = {
    Noir,
};

export default {
    name: 'ThemeSwitcher',
    components: {
        SelectButton,
        ToggleSwitch
    },
    data() {
        return {
            dropdownOpen: false,
            currentTheme: 'Noir', // 用本地狀態完全替代 $appState.theme
            iconClass: 'pi-moon',
            presets: Object.keys(presets),
            selectedPrimaryColor: 'noir',
            selectedSurfaceColor: 'zinc',
            primaryColors: [
                { name: 'noir', palette: {} },
                { name: 'emerald', palette: { 50: '#ecfdf5', 500: '#10b981' } },
                { name: 'green', palette: { 50: '#f0fdf4', 500: '#22c55e' } },
                { name: 'orange', palette: { 50: '#fff7ed', 500: '#f97316' } },
                { name: 'blue', palette: { 50: '#eff6ff', 500: '#3b82f6' } }
            ],
            surfaces: [
                { name: 'slate', palette: { 0: '#ffffff', 500: '#64748b', 950: '#020617' } },
                { name: 'gray', palette: { 0: '#ffffff', 500: '#677280', 950: '#030712' } },
                { name: 'zinc', palette: { 0: '#ffffff', 500: '#71717a', 950: '#09090b' } }
            ]
        };
    },
    methods: {
        onThemeToggler() {
            const root = document.getElementsByTagName('html')[0];
            root.classList.toggle('p-dark');
            this.iconClass = this.iconClass === 'pi-moon' ? 'pi-sun' : 'pi-moon';
        },
        getPresetExt() {
            const color = this.primaryColors.find((c) => c.name === this.selectedPrimaryColor);
            if (!color) return {};
            
            if (color.name === 'noir') {
                return {
                    semantic: {
                        primary: {
                            50: '{surface.50}', 100: '{surface.100}', 200: '{surface.200}',
                            300: '{surface.300}', 400: '{surface.400}', 500: '{surface.500}',
                            600: '{surface.600}', 700: '{surface.700}', 800: '{surface.800}',
                            900: '{surface.900}', 950: '{surface.950}'
                        }
                    }
                };
            }
            return { semantic: { primary: color.palette } };
        },
        updateColors(type, color) {
            if (type === 'primary') this.selectedPrimaryColor = color.name;
            else if (type === 'surface') this.selectedSurfaceColor = color.name;
            this.applyTheme(type, color);
        },
        applyTheme(type, color) {
            if (type === 'primary') {
                if (typeof updatePreset === 'function') updatePreset(this.getPresetExt());
            } else if (type === 'surface') {
                if (typeof updateSurfacePalette === 'function') updateSurfacePalette(color.palette);
            }
        },
        onRippleChange(value) {
            if (this.$primevue?.config) {
                this.$primevue.config.ripple = value;
            }
        },
        onPresetChange(value) {
            if (!value) return;
            this.currentTheme = value;
            const preset = presets[value];
            if (!preset) return;

            const surfacePalette = this.surfaces.find((s) => s.name === this.selectedSurfaceColor)?.palette;

            if (typeof $t === 'function' && $t()) {
                $t()
                    .preset(preset)
                    .preset(this.getPresetExt())
                    .surfacePalette(surfacePalette)
                    .use({ useDefaultOptions: true });
            }
        }
    },
    computed: {
        rippleActive() {
            return this.$primevue?.config?.ripple ?? false;
        }
    },
    mounted() {
        this.clickOutsideHandler = (e) => {
            if (this.$el && !this.$el.contains(e.target)) {
                this.dropdownOpen = false;
            }
        };
        document.addEventListener('click', this.clickOutsideHandler);
    },
    unmounted() {
        document.removeEventListener('click', this.clickOutsideHandler);
    }
};
</script>